import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Activity, ChevronLeft, ChevronRight, ArrowRight, AlertCircle, CheckCircle } from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import PageTitle from '@/components/website/PageTitle';

/* eslint-disable react/no-unescaped-entities */

// V2 Theme Colors
const C = {
  white: '#FFFFFF',
  cloud: '#F8FAFC',
  sky: '#4A6D8C',
  skyLight: '#DCE6F0',
  skyDark: '#334F6B',
  ink: '#0F172A',
  slate: '#475569',
  muted: '#94A3B8',
};

const CovidLongPage = () => {
  const navigate = useNavigate();

  const symptoms = [
    "Essoufflement persistant",
    "Fatigue intense",
    "Brouillard mental",
    "Douleurs thoraciques",
    "Palpitations",
    "Intolérance à l'effort",
    "Troubles du sommeil",
    "Anxiété et dépression"
  ];

  return (
    <div 
      className="min-h-screen" 
      style={{ backgroundColor: C.white, fontFamily: 'Figtree, sans-serif' }}
    >
      {/* Global Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500&family=Figtree:wght@400;500;600&display=swap');
        @keyframes heroBreathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        @keyframes heroBreatheShadow {
          0%, 100% {
            box-shadow: 0 0 40px rgba(14, 165, 233, 0.30),
                        0 0 80px rgba(14, 165, 233, 0.10);
          }
          50% {
            box-shadow: 0 0 60px rgba(14, 165, 233, 0.50),
                        0 0 120px rgba(14, 165, 233, 0.20);
          }
        }
        .hero-breathing-orb {
          animation: heroBreathe 4s ease-in-out infinite,
                     heroBreatheShadow 4s ease-in-out infinite;
          will-change: transform, box-shadow;
        }
      `}</style>
      
      <SiteHeaderV2 />
      
      {/* Hero Section */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <PageTitle
                kicker="Troubles respiratoires"
                title="COVID"
                highlight="Long"
                subtitle="Rééducation respiratoire pour les symptômes persistants post-COVID. Une approche douce et progressive pour retrouver votre souffle."
                align="left"
                className="mb-8"
              />
              <Link 
                to="/evaluer-respiration"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                style={{ backgroundColor: C.sky, color: C.white }}
              >
                Évaluer votre respiration
                <ChevronRight size={18} />
              </Link>
            </div>
            <div className="flex justify-center">
              <div className="hero-breathing-orb w-72 h-72 lg:w-96 lg:h-96 rounded-full overflow-hidden" style={{ border: `4px solid ${C.skyLight}` }}>
                <img 
                  src="/lungs-covid.jpg"
                  alt="Poumons - Rééducation COVID Long"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: 'center 60%' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Comprendre le COVID Long
          </h2>
          <div className="prose prose-lg max-w-none text-slate-600">
            <p className="mb-4">
              Le syndrome post-COVID, communément appelé COVID Long, se caractérise par la persistance 
              de symptômes plus de 12 semaines après l'infection initiale. Ces symptômes peuvent durer 
              des mois, voire des années, et affecter significativement la vie quotidienne.
            </p>
            <p className="mb-4">
              Les mécanismes impliqués sont multiples : inflammation persistante, dysfonctionnement du 
              système nerveux autonome, déconditionnement physique, et développement de patterns 
              respiratoires dysfonctionnels.
            </p>
          </div>
        </div>
      </section>

      {/* Symptoms */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-8" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Symptômes Respiratoires du COVID Long
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {symptoms.map((symptom, index) => (
              <div key={index} className="flex items-center gap-3 p-4 rounded-xl shadow-sm">
                <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <Activity className="w-4 h-4 text-violet-500" />
                </div>
                <span className="text-slate-700">{symptom}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rehabilitation */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            L'Approche de Rééducation
          </h2>
          <div className="rounded-2xl p-8 shadow-lg border border-slate-100">
            <p className="text-lg text-slate-600 mb-6">
              La rééducation respiratoire pour le COVID Long doit être progressive et adaptée. 
              Elle combine plusieurs approches :
            </p>
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-sky-600">1</span>
                </div>
                <div>
                  <h4 className="font-medium">Restauration du pattern respiratoire</h4>
                  <p className="text-slate-600">Correction de la respiration dysfonctionnelle installée pendant la maladie</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-sky-600">2</span>
                </div>
                <div>
                  <h4 className="font-medium">Amélioration de la tolérance au CO2</h4>
                  <p className="text-slate-600">Exercices de type Buteyko pour normaliser la chimiosensibilité</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-sky-600">3</span>
                </div>
                <div>
                  <h4 className="font-medium">Rééquilibrage du système nerveux</h4>
                  <p className="text-slate-600">Cohérence cardiaque et techniques de relaxation pour calmer le système sympathique</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-sky-600">4</span>
                </div>
                <div>
                  <h4 className="font-medium">Reconditionnement progressif</h4>
                  <p className="text-slate-600">Augmentation très graduelle de l'activité physique selon le principe du pacing</p>
                </div>
              </div>
            </div>
            <Button onClick={() => navigate('/app-landing')} className="bg-cyan-500 hover:bg-cyan-600 rounded-full">
              Accéder aux exercices adaptés
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </div>
        </div>
      </section>

      {/* Warning */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-6 h-6 text-slate-600" />
            </div>
            <div>
              <h3 className="font-medium mb-2">Important</h3>
              <p className="text-slate-600">
                Si vous souffrez de COVID Long, consultez un professionnel de santé avant de débuter 
                tout programme d'exercices. La rééducation doit être adaptée à votre condition et 
                progresser à votre rythme. En cas d'aggravation des symptômes après un exercice, 
                réduisez l'intensité et consultez votre médecin.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SiteFooterV2 />
    </div>
  );
};

export default CovidLongPage;
